'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { useModelsApi, type ModelInfo } from '@enter/api';

type ModelsApi = ReturnType<typeof useModelsApi>;
type Snapshot = {
    models: ModelInfo[];
    status: 'idle' | 'loading' | 'ready' | 'error';
};
const EMPTY_SNAPSHOT: Snapshot = { models: [], status: 'idle' };

// 同一 API 作用域下的创建入口和模型选择器共享请求及快照，不共享用户级数据。
const stores = new WeakMap<ModelsApi, ReturnType<typeof createModelsStore>>();

function createModelsStore(api: ModelsApi) {
    let snapshot = EMPTY_SNAPSHOT;
    let pending: Promise<void> | null = null;
    const listeners = new Set<() => void>();
    const publish = (next: Snapshot) => {
        snapshot = next;
        for (const listener of listeners) listener();
    };
    const load = (): Promise<void> => {
        if (pending) return pending;
        publish({ models: snapshot.models, status: 'loading' });
        pending = (async () => {
            try {
                const response = await api.getModels();
                if (
                    response.code !== 0 ||
                    !Array.isArray(response.data?.models)
                )
                    throw new Error(
                        response.message || 'Failed to load models',
                    );
                publish({ models: response.data.models, status: 'ready' });
            } catch (error) {
                console.error('Failed to fetch models:', error);
                publish({ models: [], status: 'error' });
            }
        })().finally(() => {
            pending = null;
        });
        return pending;
    };
    return {
        getSnapshot: () => snapshot,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        load,
    };
}

export function useModels() {
    const api = useModelsApi();
    let store = stores.get(api);
    if (!store) {
        store = createModelsStore(api);
        stores.set(api, store);
    }
    const snapshot = useSyncExternalStore(
        store.subscribe,
        store.getSnapshot,
        () => EMPTY_SNAPSHOT,
    );
    useEffect(() => {
        if (store.getSnapshot().status === 'idle') void store.load();
    }, [store]);
    return {
        models: snapshot.models,
        isLoading: snapshot.status === 'idle' || snapshot.status === 'loading',
        failed: snapshot.status === 'error',
        retry: store.load,
    };
}
